<template>
  <div>
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <div class="relative inline-flex w-auto">
      <!-- Country Code Dropdown -->
      <div class="relative">
        <button
          type="button"
          @click="isOpen = !isOpen"
          class="h-full px-3 py-2 border rounded-l-lg flex items-center gap-2 transition-colors whitespace-nowrap"
          :class="[
            error
              ? 'border-red-300 dark:border-red-700'
              : 'border-gray-200 dark:border-gray-700',
            readonly
              ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
              : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
          ]"
        >
          <span v-if="selectedCountry">{{ selectedCountry.flag }}</span>
          <span class="text-gray-400">▼</span>
        </button>

        <!-- Dropdown Menu -->
        <div
          v-if="isOpen"
          class="absolute z-10 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <!-- Search Input -->
          <div class="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search countries..."
              class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
              @click.stop
            />
          </div>

          <!-- Countries List -->
          <div class="max-h-60 overflow-y-auto">
            <button
              v-for="country in filteredCountries"
              :key="country.code"
              @click="handleCountrySelect(country)"
              class="w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700"
              
            >
              <span>{{ country.flag }}</span>
              <span>{{ country.name }}</span>
              <span class="ml-auto text-gray-500 dark:text-gray-400">{{ country.dialCode }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Phone Number Input -->
      <input
        type="tel"
        v-model="phoneNumber"
        @input="handlePhoneInput"
        @keydown="handleKeyDown"
        @blur="handleBlur"
        :placeholder="placeholder"
        :readonly="readonly"
        class="w-full px-3 py-2 border border-l-0 rounded-r-lg text-gray-900 dark:text-white"
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900'
        ]"
      />
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

interface Country {
  code: string
  flag: string
  dialCode: string
  name: string
}

const props = defineProps<{
  modelValue: string | null | number
  countryCode?: string
  label?: string
  error?: string | null
  readonly?: boolean
  placeholder?: string
  required?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'update:countryCode', value: string): void
  (e: 'blur'): void
}>()

const isOpen = ref(false)
const searchQuery = ref('')

// List of countries with flags and dial codes
const countries: Country[] = [
  //{ code: '1', flag: '🇺🇸', dialCode: '+1', name: 'United States' },
  //{ code: '44', flag: '🇬🇧', dialCode: '+44', name: 'United Kingdom' },
  //{ code: '91', flag: '🇮🇳', dialCode: '+91', name: 'India' },
  { code: '960', flag: '🇲🇻', dialCode: '+960', name: 'Maldives' },
]

const selectedCountry = ref<Country>()
/*const selectedCountry = computed(() => {
  return countries.find(c => c.code === props.countryCode) || countries[0]
})*/

const filteredCountries = computed(() => {
  if (!searchQuery.value) return countries
  const query = searchQuery.value.toLowerCase()
  return countries.filter(country => 
    country.name.toLowerCase().includes(query) ||
    country.dialCode.includes(query)
  )
})

const handleCountrySelect = (country: Country) => {
  selectedCountry.value = country
  emit('update:countryCode', country.code)
  isOpen.value = false
  searchQuery.value = ''
  updateModelValue()
}

const handlePhoneInput = (e: Event) => {
  const input = e.target as HTMLInputElement
  // Only allow integers by removing any non-digit characters
  const value = input.value.replace(/\D/g, '')
  
  // Update input value to show only integers
  input.value = value
  
  updateModelValue()
}

const handleKeyDown = (e: KeyboardEvent) => {
  // Prevent non-numeric input (including decimal point)
  if (
    !/^\d$/.test(e.key) && // Not a number
    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key) // Not a control key
  ) {
    e.preventDefault()
  }
}

const handleBlur = () => {
  setTimeout(() => {
    isOpen.value = false
    emit('blur')
  }, 200)
}

const phoneNumber = ref()

const updateModelValue = () => {
  if (selectedCountry.value && phoneNumber.value) {
    emit('update:modelValue', `${selectedCountry.value.dialCode}-${phoneNumber.value}`)
  } else {
    emit('update:modelValue', null)
  }
}

watch(() => props.modelValue, (newValue) => {
  let parts: string[] = [];
  if (typeof newValue === 'string') {
    parts = newValue.split('-');
  }
  phoneNumber.value = parts[1] || '';
  let c = countries.find(c => c.dialCode === parts[0])
  if (c)
    selectedCountry.value = c;
});

onMounted(() => {
  if (props.modelValue && typeof props.modelValue === 'string' && props.modelValue.includes('-')) {
    console.log('props.modelValue', props.modelValue)
    const parts = props.modelValue ? props.modelValue.split('-') : [];
  phoneNumber.value = parts[1] || '';
  let c = countries.find(c => c.dialCode === parts[0])
  if (c)
    selectedCountry.value = c;
  } else {
    handleCountrySelect(countries[0])
  }
})

</script>

<style scoped>
/* Add your styles here */
</style>